import React, { useEffect, useState } from 'react';
import { useBrandingStore } from '../store/brandingStore';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { apiService } from '../services/apiService';
import { CompanyInfoCard } from './common/CompanyInfoCard';
import { resolveLogoUrl } from '../utils/urlUtils';


// Компонент для загрузки файлов с drag&drop
const FileUploadArea = ({ 
  onFileSelect, 
  onFilesDrop, 
  selectedFile, 
  onRemoveFile,
  existingLogoUrl
}: { 
  onFileSelect: (file: File) => void; 
  onFilesDrop: (files: FileList) => void;
  selectedFile: File | null;
  onRemoveFile: () => void;
  existingLogoUrl?: string;
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFilesDrop(files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  };

  // Показываем блок только если есть выбранный файл или реально существующий логотип
  const hasValidLogo = selectedFile || (existingLogoUrl && existingLogoUrl.trim());
  
  if (hasValidLogo) {
    return (
      <div className="bg-[#ffffff] relative rounded-2xl shrink-0 w-full py-[24px] px-[24px] border-2 border-solid border-[#e2e4e9]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              {selectedFile ? (
                selectedFile.type.startsWith('image/') ? (
                  <img 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )
              ) : existingLogoUrl && existingLogoUrl.trim() ? (
                <img 
                  src={resolveLogoUrl(existingLogoUrl)} 
                  alt="Текущий логотип" 
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>
            <div>
              {selectedFile ? (
                <>
                  <div className="text-sm font-medium text-gray-900">{selectedFile.name}</div>
                  <div className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</div>
                </>
              ) : existingLogoUrl && existingLogoUrl.trim() ? (
                <>
                  <div className="text-sm font-medium text-gray-900">Текущий логотип</div>
                  <div className="text-xs text-gray-500">Загружен ранее</div>
                </>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onRemoveFile}
            className="text-red-500 hover:text-red-700 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-[#ffffff] relative rounded-2xl shrink-0 w-full py-[108px] px-[48px] border-2 border-dashed transition-colors ${
        isDragOver ? 'border-[#e16349] bg-[#fef7f6]' : 'border-[#e2e4e9]'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="text-center">
        <div className="text-[#525866] text-sm mb-4">
          {isDragOver ? 'Отпустите файл для загрузки' : 'Перетащите файл сюда или нажмите для выбора'}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
        />
        <label htmlFor="file-upload" className="text-white h-[44px] px-[24px] rounded-2xl cursor-pointer transition-colors inline-flex items-center justify-center font-['Inter:Medium',_sans-serif] font-medium text-[16px] tracking-[-0.176px]" style={{ backgroundColor: useBrandingStore.getState().getPrimaryColor() }}>
          Выбрать файл
        </label>
      </div>
    </div>
  );
};

const FigmaTextInput = ({ label, value, onChange, placeholder }: { 
  label: string; 
  value: string; 
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
    <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
      <p className="block leading-[20px]">{label}</p>
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] px-[24px] h-[44px] border border-[#e2e4e9] focus:outline-none"
      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary, #e16349)'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e4e9'; }}
      placeholder={placeholder || "Введите название компании"}
    />
  </div>
);

// Удалена неиспользуемая константа

interface BrandingFormData {
  companyName: string;
  logo: File | null;
  existingLogoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  benefits: string;
  aboutCompany: string;
}

/**
 * Branding Page Component
 * Контент-карточка для вкладки "Брендирование" (без внешних page-оберток)
 */
export function BrandingPage() {
  const { data: brandingData, companyData, loadBranding, loadCompanyData, setBranding, getDefaultCompanyName, isLoading, error: brandingError } = useBrandingStore();
  const colors = useBrandingColors();
  const [formData, setFormData] = useState<BrandingFormData>({
    companyName: '',
    logo: null,
    existingLogoUrl: undefined,
    primaryColor: useBrandingStore.getState().getPrimaryColor(),
    secondaryColor: useBrandingStore.getState().getAiColor(),
    benefits: '',
    aboutCompany: ''
  });
  // Удалены неиспользуемые состояния
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Загружаем данные брендирования и компании при монтировании компонента
  useEffect(() => {
    loadBranding();
    loadCompanyData();
    apiService.getMyAccountSettings().then((settings: any) => {
      if (settings?.aboutCompany) {
        setFormData(prev => ({ ...prev, aboutCompany: settings.aboutCompany }));
      }
    }).catch(() => {});
  }, [loadBranding, loadCompanyData]);

  // Обновляем форму при изменении данных из store
  useEffect(() => {
    console.log('🔍 BrandingPage: brandingData changed:', brandingData);
    if (brandingData) {
      setFormData(prev => ({
        ...prev,
        companyName: brandingData.companyName || '',
        existingLogoUrl: brandingData.logoUrl,
        primaryColor: brandingData.primaryColor || useBrandingStore.getState().getPrimaryColor(),
        secondaryColor: (brandingData.secondaryColor && brandingData.secondaryColor.trim()) || useBrandingStore.getState().getAiColor(),
        benefits: brandingData.benefits || ''
      }));
      console.log('🔍 BrandingPage: form data updated with branding data');
    }
  }, [brandingData]);

  // Очистка URL при размонтировании компонента
  useEffect(() => {
    return () => {
      if (formData.logo) {
        URL.revokeObjectURL(URL.createObjectURL(formData.logo));
      }
    };
  }, [formData.logo]);

  const handleFileSelect = (file: File) => {
    console.log('Selected file:', file);
    
    // Валидация размера файла (максимум 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Размер файла не должен превышать 5MB');
      return;
    }
    
    // Валидация типа файла
    if (!file.type.startsWith('image/')) {
      setError('Выберите файл изображения');
      return;
    }
    
    setError(null);
    setSuccess(null);
    setFormData(prev => ({ ...prev, logo: file }));
  };

  const handleFilesDrop = (files: FileList) => {
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleCompanyNameChange = (value: string) => {
    setFormData(prev => ({ ...prev, companyName: value }));
  };

  const handleRemoveFile = async () => {
    try {
      // Если есть существующий логотип на сервере, удаляем его через API
      if (formData.existingLogoUrl) {
        await apiService.deleteMyCompanyLogo();
        // Обновляем данные в store после удаления
        await loadBranding();
      }
      
      setFormData(prev => {
        // Очищаем URL для предотвращения утечек памяти
        if (prev.logo) {
          URL.revokeObjectURL(URL.createObjectURL(prev.logo));
        }
        return { ...prev, logo: null, existingLogoUrl: undefined };
      });
      
      setSuccess('Логотип успешно удален');
      setError(null);
    } catch (error) {
      console.error('Ошибка при удалении логотипа:', error);
      setError('Не удалось удалить логотип');
      setSuccess(null);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!formData.companyName.trim()) {
      setError('Название компании обязательно для заполнения');
      return;
    }

    try {
      setIsSaving(true);
      
      let updatedBranding: any;
      
      // Если выбран файл логотипа, загружаем его отдельно через multipart API
      if (formData.logo) {
        try {
          console.log('🔍 Uploading logo via multipart API...');
          updatedBranding = await apiService.uploadMyCompanyLogo(formData.logo);
          console.log('✅ Logo uploaded successfully:', updatedBranding);
        } catch (error) {
          console.error('❌ Error uploading logo:', error);
          setError('Ошибка при загрузке логотипа');
          return;
        }
      }
      
      // Подготавливаем данные для обновления брендинга (без логотипа)
      const brandingUpdateData: any = {
        companyName: formData.companyName.trim(),
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        benefits: formData.benefits,
      };

      console.log('🔍 Saving branding data:', brandingUpdateData);
      
      updatedBranding = await apiService.updateMyCompanyBranding(brandingUpdateData);
      
      setBranding(updatedBranding);

      await apiService.updateMyAccountSettings({ aboutCompany: formData.aboutCompany });
      
      setSuccess('Брендирование успешно сохранено');
      
    } catch (error) {
      console.error('❌ Error saving branding:', error);
      setError(error instanceof Error ? error.message : 'Ошибка при сохранении брендирования');
    } finally {
      setIsSaving(false);
    }
  };

  	return (
		<div className="bg-[#f5f6f1] box-border content-stretch flex flex-col gap-6 items-start justify-start p-[24px] pb-8 relative rounded-[24px] shrink-0 w-full h-full overflow-y-auto">
      {/* Header */}
      <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
        <p className="block leading-[30px] whitespace-pre">Брендирование</p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div 
          className="px-4 py-3 rounded"
          style={{
            backgroundColor: `${colors.primary}20`,
            border: `1px solid ${colors.primary}`,
            color: colors.primary
          }}
        >
          Загрузка данных брендинга...
        </div>
      )}

      {/* Error/Success Messages */}
      {brandingError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Ошибка загрузки брендинга: {brandingError}
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      

     

      {/* Form */}
      <form onSubmit={handleSubmit} className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-0 relative shrink-0 w-full">
        {/* Company Name and File Upload */}
        <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-0 relative shrink-0 w-full">
          {/* Company Name Input */}
          <FigmaTextInput
            label="Название компании"
            value={formData.companyName}
            onChange={handleCompanyNameChange}
            placeholder={`Введите название компании (по умолчанию: ${getDefaultCompanyName()})`}
          />

          {/* File Upload Section */}
          <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-0 relative shrink-0 w-full">
            <FileUploadArea 
              onFileSelect={handleFileSelect}
              onFilesDrop={handleFilesDrop}
              selectedFile={formData.logo}
              onRemoveFile={handleRemoveFile}
              existingLogoUrl={formData.existingLogoUrl}
            />
            
            {/* Disclaimer */}
            <div className="bg-[#e2e4e9] relative rounded-2xl shrink-0 w-full">
              <div className="flex flex-row items-center justify-center relative size-full">
                <div className="box-border content-stretch flex flex-row gap-2.5 items-center justify-center p-[16px] relative w-full">
                  <div className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[12px] text-left w-full">
                    <p className="block leading-[16px]">
                      Загружая логотип вы подтверждаете, что имеете право на его использование и не нарушаете прав третьих лиц
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Color Selection (Primary + Secondary on one row) */}
          <div className="box-border content-stretch flex flex-col md:flex-row gap-6 items-start justify-start p-0 relative w-full">
            <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative w-full md:w-1/2">
              <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
                <p className="block leading-[20px]">Основной цвет</p>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, primaryColor: e.target.value }));
                  }}
                  className="w-12 h-12 rounded-lg border border-[#e2e4e9] cursor-pointer"
                />
                <div className="text-sm text-[#525866]">
                  {formData.primaryColor}
                </div>
              </div>
            </div>

            <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative w-full md:w-1/2">
              <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
                <p className="block leading-[20px]">Второстепенный цвет (красный / AI)</p>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.secondaryColor}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, secondaryColor: e.target.value }));
                  }}
                  className="w-12 h-12 rounded-lg border border-[#e2e4e9] cursor-pointer"
                />
                <div className="text-sm text-[#525866]">
                  {formData.secondaryColor}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative w-full">
          <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
            <p className="block leading-[20px]">Бенефиты компании</p>
          </label>
          <textarea
            value={formData.benefits}
            onChange={(e) => setFormData(prev => ({ ...prev, benefits: e.target.value }))}
            placeholder="Опишите бенефиты компании (ДМС, оплата спорта, обучение...). Будут автоматически подставляться в поле «Что входит в соц.пакет?» при создании новых вакансий."
            rows={5}
            className="w-full border border-[#e2e4e9] rounded-xl p-3 text-sm text-[#0a0d14] resize-y focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent"
          />
        </div>

        {/* About Company Section */}
        <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative w-full">
          <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
            <p className="block leading-[20px]">О компании</p>
          </label>
          <textarea
            value={formData.aboutCompany}
            onChange={(e) => setFormData(prev => ({ ...prev, aboutCompany: e.target.value }))}
            placeholder="Расскажите о компании и команде. Этот текст будет автоматически подставляться в поле «О компании и команде» при создании новых вакансий."
            rows={6}
            className="w-full border border-[#e2e4e9] rounded-xl p-3 text-sm text-[#0a0d14] resize-y focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent"
          />
          <p className="text-xs text-[#525866]">
            Сохраняется для вашего аккаунта и автоматически подставляется в раздел «О компании и команде» при создании вакансий.
          </p>
        </div>

        {/* Success Message (shown above action buttons) */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded w-full">
            {success}
          </div>
        )}

        {/* Save Button */}
        <button
          type="submit"
          disabled={isSaving}
          className={`h-[44px] relative rounded-2xl shrink-0 px-[24px] transition-colors ${
            isSaving 
              ? 'bg-gray-400 cursor-not-allowed' 
              : ''
          }`}
          style={!isSaving ? { backgroundColor: useBrandingStore.getState().getPrimaryColor() } : undefined}
          data-name="Buttons [1.0]"
        >
          <div className="flex flex-row items-center justify-center overflow-clip relative size-full">
            <div className="box-border content-stretch flex flex-row gap-1 h-[44px] items-center justify-center p-[10px] relative w-full">
              <div
                className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0"
                data-name="Text"
              >
                <div className="css-rpndqk font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#ffffff] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                  <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                    {isSaving ? 'Сохранение...' : 'Сохранить'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </button>
      </form>
    </div>
  );
}